import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Router } from './router';
import { Storage, AccountManager, api, showToast, EventBus, TaskTracker } from './utils';

interface NavItem {
  key: string;
  icon: string;
  label: string;
  path?: string;
  badge?: string;
}

export class MangaApp extends LitElement {
  static override styles = css`
    *, *::before, *::after { box-sizing: border-box; }
    * { scrollbar-width: none; -ms-overflow-style: none; }
    ::-webkit-scrollbar { display: none; }
    :host {
      display: flex;
      width: 100%;
      height: 100dvh;
      font-family: var(--font-body);
      color: var(--text-primary);
    }

    /* ===== Sidebar ===== */
    .sidebar {
      width: 240px;
      background: var(--bg-surface);
      border-right: 1px solid var(--border-color);
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      transition: width 300ms var(--ease-out-quart);
      z-index: 50;
      overflow: hidden;
    }
    .sidebar.collapsed { width: 64px; }
    .collapsed .nav-text, .collapsed .nav-group-label,
    .collapsed .nav-badge, .collapsed .brand-text,
    .collapsed .brand-tagline { display: none; }
    .collapsed .nav-item { justify-content: center; padding: 10px; }
    .collapsed .brand { justify-content: center; padding: 14px 0; gap: 0; }
    .collapsed .brand-logo { width: 28px; height: 28px; }

    /* Brand */
    .brand {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 24px 20px 12px;
      cursor: pointer;
      transition: opacity 200ms var(--ease-out-quart);
    }
    .brand:hover { opacity: 0.85; }
    .brand-logo {
      width: 36px; height: 36px;
      object-fit: contain;
      flex-shrink: 0;
      transition: transform 200ms var(--ease-out-quart);
    }
    .brand:hover .brand-logo { transform: scale(1.04); }
    .brand-info { display: flex; flex-direction: column; min-width: 0; }
    .brand-text {
      font-size: 1.05rem;
      font-weight: 800;
      letter-spacing: -0.025em;
      color: var(--text-primary);
      line-height: 1.2;
    }
    .brand-tagline {
      font-size: 0.6rem;
      color: var(--text-tertiary);
      letter-spacing: 0.12em;
      font-weight: 500;
      margin-top: 3px;
      text-transform: uppercase;
    }

    /* Nav */
    .nav-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 8px 12px 20px;
    }
    .nav-group-label {
      font-size: 0.6rem;
      font-weight: 700;
      color: var(--text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.1em;
      padding: 20px 12px 6px;
      margin-top: 2px;
      border-top: 1px solid var(--border-light);
      user-select: none;
    }
    .nav-group-label:first-child { padding-top: 8px; margin-top: 0; border-top: none; }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 9px 12px;
      margin-bottom: 2px;
      border: none;
      background: transparent;
      color: var(--text-secondary);
      border-radius: var(--radius-md);
      cursor: pointer;
      font: inherit;
      font-size: 0.84rem;
      font-weight: 500;
      width: 100%;
      text-align: left;
      transition: var(--transition-fast);
      position: relative;
      outline: none;
    }
    .nav-item:hover, .nav-item:focus-visible {
      background: var(--bg-surface-hover);
      color: var(--text-primary);
    }
    .nav-item.active {
      background: transparent;
      color: var(--primary-600);
      font-weight: 600;
    }
    .nav-item.active::before {
      content: '';
      position: absolute; left: 0; top: 50%;
      transform: translateY(-50%);
      width: 2.5px; height: 18px;
      background: var(--primary-500);
      border-radius: 0 2px 2px 0;
    }
    .nav-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 1.15rem;
      width: 22px;
      text-align: center;
      flex-shrink: 0;
      transition: color 150ms var(--ease-out-quart);
    }
    .nav-text { flex: 1; white-space: nowrap; overflow: hidden; }
    .nav-badge {
      font-size: 0.58rem; padding: 1px 6px;
      border-radius: var(--radius-full);
      background: var(--primary-50);
      color: var(--primary-600);
      font-weight: 700; flex-shrink: 0;
    }

    /* Sidebar footer */
    .sidebar-footer {
      padding: 10px 12px 12px;
      border-top: 1px solid var(--border-light);
    }
    .collapse-btn {
      display: flex; align-items: center; justify-content: center; gap: 6px;
      width: 100%; padding: 7px;
      border: none; background: transparent;
      color: var(--text-tertiary); cursor: pointer;
      border-radius: var(--radius-md);
      transition: all 0.2s ease;
      font-size: 0.75rem;
      font-family: inherit;
      outline: none;
    }
    .collapse-btn:hover, .collapse-btn:focus-visible {
      background: var(--bg-surface-hover);
      color: var(--text-secondary);
    }
    .collapse-btn .mi {
      font-family: 'Material Symbols Outlined';
      font-size: 16px;
      transition: transform 0.3s ease;
    }
    .sidebar.collapsed .collapse-btn .mi { transform: rotate(180deg); }
    .logout-btn {
      display: flex; align-items: center; justify-content: center; gap: 6px;
      width: 100%; padding: 7px;
      border: none; background: transparent;
      color: var(--text-tertiary); cursor: pointer;
      border-radius: var(--radius-md);
      transition: all 0.2s ease;
      font-size: 0.75rem;
      font-family: inherit;
      outline: none;
      margin-bottom: 4px;
    }
    .logout-btn:hover { background: var(--error-bg); color: var(--error-text); }
    .billing-bar {
      display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 4px 8px;
      padding: 10px 12px; margin-bottom: 8px;
      background: var(--bg-panel);
      border-radius: var(--radius-md); border: 1px solid var(--border-light);
      font-size: 0.74rem; color: var(--text-secondary); cursor: pointer;
      transition: var(--transition-fast);
    }
    .billing-cell { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; justify-content: center; }
    .billing-bar:hover { background: var(--neutral-200); border-color: var(--border-color); }
    .billing-bar.conc-ok .mi { color: var(--success-text); }
    .billing-bar.conc-warn .mi { color: var(--warning-text); }
    .billing-bar.conc-full .mi { color: var(--error-text); }
    .billing-bar.conc-full {
      background: var(--error-bg); border-color: var(--error-text);
    }
    .billing-bar .mi { font-family: 'Material Symbols Outlined'; font-size: 14px; color: var(--primary-500); flex-shrink: 0; }
    .billing-val { font-weight: 700; color: var(--text-primary); white-space: nowrap; }
    .billing-sep { color: var(--border-color); text-align: center; }
    .collapsed .billing-bar .billing-val, .collapsed .billing-bar .billing-sep { display: none; }
    .collapsed .billing-bar { grid-template-columns: 1fr; justify-items: center; padding: 8px 4px; gap: 2px; }
    .collapsed .billing-cell { justify-content: center; }
    .user-row {
      display: flex; align-items: center; gap: 10px;
      padding: 8px 10px; margin-bottom: 6px;
    }
    .user-avatar {
      width: 32px; height: 32px; border-radius: 50%;
      background: var(--primary-500);
      display: flex; align-items: center; justify-content: center;
      font-size: 0.85rem; font-weight: 800; color: var(--text-on-primary);
      cursor: pointer; flex-shrink: 0; overflow: hidden;
      transition: transform 150ms var(--ease-out-quart);
    }
    .user-avatar:hover { transform: scale(1.05); }
    .user-name {
      font-size: 0.78rem; font-weight: 600; color: var(--text-primary);
      cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      flex: 1; min-width: 0;
    }
    .user-name:hover { color: var(--primary-600); }
    .collapsed .user-name { display: none; }
    .collapsed .logout-btn { display: none; }
    .collapsed .user-row { justify-content: center; padding: 8px 4px; }

    .logout-btn .mi { font-family: 'Material Symbols Outlined'; font-size: 16px; }

    /* ===== Main ===== */
    .main {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-width: 0;
      background-color: var(--bg-app);
      position: relative;
    }
    .main-header {
      height: 54px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 28px;
      background: var(--bg-surface);
      border-bottom: 1px solid var(--border-color);
      flex-shrink: 0;
      z-index: 10;
    }
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.88rem;
      color: var(--text-tertiary);
      font-weight: 500;
    }
    .breadcrumb-current {
      color: var(--text-primary);
      font-weight: 700;
      letter-spacing: -0.015em;
    }
    .header-actions { display: flex; align-items: center; gap: 4px; }
    .task-indicator {
      display: flex; align-items: center; gap: 6px;
      padding: 4px 12px; border-radius: 20px;
      background: var(--primary-50); color: var(--primary-600);
      font-size: 0.78rem; font-weight: 500; cursor: pointer;
      transition: var(--transition-fast);
    }
    .task-indicator:hover { background: var(--primary-100); }
    .indicator-dot {
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--primary-500);
      animation: pulse-dot 1.5s ease-in-out infinite;
    }
    @keyframes pulse-dot {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.8); }
    }
    .header-btn {
      width: 34px; height: 34px;
      border: none; background: transparent;
      color: var(--text-secondary);
      border-radius: var(--radius-md);
      cursor: pointer; transition: var(--transition-fast);
      display: flex; align-items: center; justify-content: center;
      font-size: 1rem;
      outline: none;
    }
    .header-btn:hover, .header-btn:focus-visible {
      background: var(--bg-surface-hover);
      color: var(--text-primary);
    }

    .main-content {
      flex: 1;
      overflow: hidden;
    }

    /* Mobile */
    .sidebar-overlay { display: none; }
    .mobile-toggle { display: none; }

    @media (max-width: 960px) {
      .sidebar {
        position: fixed;
        top: 0; left: 0; bottom: 0;
        width: min(82vw, 280px);
        transform: translateX(-105%);
        box-shadow: var(--shadow-lg);
        z-index: 200;
        transition: transform 300ms var(--ease-out-quart);
      }
      .sidebar.mobile-open { transform: translateX(0); }
      .sidebar.collapsed { width: min(82vw, 280px); }
      .sidebar-overlay {
        display: block;
        position: fixed; inset: 0;
        background: var(--overlay-bg);
        opacity: 0; visibility: hidden;
        transition: all 250ms var(--ease-out-quart);
        z-index: 190;
      }
      .sidebar-overlay.open { opacity: 1; visibility: visible; }
      .mobile-toggle { display: flex; }
    }

    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;

  @state() private collapsed = false;
  @state() private mobileOpen = false;
  @state() private mobile = false;
  @state() private currentPage = '';
  @state() private billingInfo: {balance:number; running:number; max_concurrency:number; storage_used?:number; max_storage_gb?:number; storage_limit_bytes?:number; storage_daily_cost?:number; storage_suspended_at?:string|null} = {balance:0, running:0, max_concurrency:0};
  @state() private trafficInfo: {total_bytes:number; free_bytes:number; free_gb:number; cdn_bytes:number; api_bytes:number; charged_amount:number; charged_bytes:number; price_per_gb:number} | null = null;
  private _billingTimer: any = null;
  @state() private userName = '';
  @state() private userAvatar = '';
  @state() private themeMode: 'light' | 'dark' | 'auto' = 'light';
  private nav: NavItem[] = [
    { key: 'dashboard', icon: 'home', label: '工作台', path: '/' },
    { key: 'create', icon: 'add_circle', label: '创建项目', path: '/create' },
    { key: 'projects', icon: 'folder_open', label: '项目管理', path: '/projects' },
    { key: 'regular', icon: 'movie', label: '常规漫剧', path: '/project/regular' },
    { key: 'commentary', icon: 'mic', label: '解说漫剧', path: '/project/commentary' },
    { key: 'char-lib', icon: 'person', label: '角色库', path: '/library/characters' },
    { key: 'scene-lib', icon: 'landscape', label: '场景库', path: '/library/scenes' },
    { key: 'prop-lib', icon: 'swords', label: '道具库', path: '/library/props' },
    { key: 'prompts', icon: 'auto_awesome', label: '提示词模板', path: '/library/prompts' },
    { key: 'voice-lib', icon: 'record_voice_over', label: '音色库', path: '/library/voices' },
    { key: 'dubbing', icon: 'headphones', label: '配音工作台', path: '/dubbing/select' },
    { key: 'billing', icon: 'account_balance_wallet', label: '我的账单', path: '/billing' },
    { key: 'model-selector', icon: 'smart_toy', label: '模型选择', path: '/model-selector' },
    { key: 'tasks', icon: 'assignment', label: '任务记录', path: '/tasks' },
  ];

  private readonly handleResize = () => {
    const m = window.innerWidth <= 960;
    if (m !== this.mobile) { this.mobile = m; this.mobileOpen = false; }
  };

  constructor() {
    super();
    this.collapsed = Storage.get<boolean>('sidebarCollapsed', false);
    this.themeMode = Storage.get<'light' | 'dark' | 'auto'>('theme', 'light');
  }

  private toggle() {
    if (this.mobile) { this.mobileOpen = !this.mobileOpen; }
    else { this.collapsed = !this.collapsed; Storage.set('sidebarCollapsed', this.collapsed); }
  }

  private _cycleTheme() {
    const order: Array<'light' | 'dark' | 'auto'> = ['light', 'dark', 'auto'];
    this.themeMode = order[(order.indexOf(this.themeMode) + 1) % 3];
    Storage.set('theme', this.themeMode);
    this._applyTheme();
  }

  private _applyTheme() {
    let effective = this.themeMode;
    if (effective === 'auto') {
      effective = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    const root = document.documentElement;
    root.classList.add('theme-transitioning');
    if (effective === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
    const mc = document.querySelector('meta[name="theme-color"]');
    if (mc) mc.setAttribute('content', effective === 'dark' ? '#1a1c2a' : '#f8f6f3');
    setTimeout(() => root.classList.remove('theme-transitioning'), 350);
  }

  private get _themeIcon(): string {
    if (this.themeMode === 'dark') return 'dark_mode';
    if (this.themeMode === 'auto') return 'brightness_auto';
    return 'light_mode';
  }

  private get _themeTitle(): string {
    if (this.themeMode === 'dark') return '当前：暗黑模式（点击切换为跟随系统）';
    if (this.themeMode === 'auto') return '当前：跟随系统（点击切换为浅色模式）';
    return '当前：浅色模式（点击切换为暗黑模式）';
  }

  private async nav_to(path: string, key?: string) {
    if (key === 'create') {
      const c = Router.getCurrentPath();
      const isOnCreateSubPage = (c === '/project/regular') || (c === '/project/commentary') || (c === '/project/new') || (c === '/create');
      if (isOnCreateSubPage) { if (this.mobile) this.mobileOpen = false; return; }
      Router.navigate('/create');
      if (this.mobile) this.mobileOpen = false;
      return;
    }
    if (key === 'regular') {
      const lastId = localStorage.getItem('lastRegularProject');
      if (lastId) {
        try {
          const res = await api(`/api/projects/${lastId}`, { silent: true });
          if (res.ok) {
            Router.navigate(`/project/regular/${lastId}`);
          } else {
            localStorage.removeItem('lastRegularProject');
            Router.navigate('/projects');
          }
        } catch {
          Router.navigate(`/project/regular/${lastId}`);
        }
      } else {
        Router.navigate('/projects');
      }
    } else if (key === 'commentary') {
      const lastPath = localStorage.getItem('lastCommentaryPath');
      const lastId = localStorage.getItem('lastCommentaryProject');
      const candidateId = lastPath?.match(/\/([\w-]+)$/)?.[1] || lastId;
      if (candidateId) {
        try {
          const res = await api(`/api/projects/${candidateId}`, { silent: true });
          if (res.ok) {
            Router.navigate(lastPath || `/project/commentary-storyboard/${candidateId}`);
          } else {
            localStorage.removeItem('lastCommentaryPath');
            localStorage.removeItem('lastCommentaryProject');
            showToast('您尚未打开过解说漫剧应用', 'info');
          }
        } catch {
          Router.navigate(lastPath || `/project/commentary-storyboard/${candidateId}`);
        }
      } else {
        showToast('您尚未打开过解说漫剧应用', 'info');
      }
    } else {
      Router.navigate(path);
    }
    if (this.mobile) this.mobileOpen = false;
  }

  private isActive(path?: string, key?: string): boolean {
    if (!path && !key) return false;
    const c = Router.getCurrentPath();
    if (key === 'regular') return c.startsWith('/project/regular/');
    if (key === 'commentary') return c.startsWith('/project/commentary-storyboard/') || (c.startsWith('/dubbing/') && c !== '/dubbing/select');
    if (path === '/') return c === '/' || c === '/dashboard';
    if (path === '/create') return c === '/create' || c === '/project/regular' || c === '/project/new' || c === '/project/commentary';
    return c === path || c.startsWith(path + '/');
  }

  private renderNavItem(item: NavItem): unknown {
    const active = this.isActive(item.path, item.key);
    return html`
      <button class="nav-item ${active ? 'active' : ''}"
        @click=${() => this.nav_to(item.path!, item.key)}
        aria-current=${active ? 'page' : 'false'}>
        <span class="nav-icon" aria-hidden="true">${item.icon}</span>
        <span class="nav-text">${item.label}</span>
        ${item.badge ? html`<span class="nav-badge">${item.badge}</span>` : nothing}
      </button>
    `;
  }

  private unsubRoute?: () => void;

  private readonly _onSystemThemeChange = () => {
    if (this.themeMode === 'auto') this._applyTheme();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    this._applyTheme();
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this._onSystemThemeChange);
    this.handleResize();
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.unsubRoute = Router.onChange(e => {
      this.currentPage = e.path; this.requestUpdate(); this._loadBilling();
      const _p = e.path;
      if (_p === '/project/regular' || _p === '/project/new' || _p.startsWith('/project/commentary')) {
        localStorage.setItem('lastCreateSubPage', _p + (window.location.search || ''));
      }
      if (_p === '/create') {
        localStorage.removeItem('lastCreateSubPage');
      }
    });
    this._startBillingPoll();
    EventBus.on('concurrency:change', (ev: any) => {
      const d = ev.payload || ev;
      if (d.running !== undefined) {
        this.billingInfo = { ...this.billingInfo, running: d.running, max_concurrency: d.max ?? this.billingInfo.max_concurrency };
      }
    });
    EventBus.on('tasktracker:change', () => this.requestUpdate());
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.handleResize);
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this._onSystemThemeChange);
    this.unsubRoute?.();
    if (this._billingTimer) clearInterval(this._billingTimer);
    this._stopBillingPoll();
  }

  private _billingPollActive = false;

  private _startBillingPoll() {
    if (this._billingPollActive) return;
    this._billingPollActive = true;
    this._loadBilling();
    this._billingTimer = setInterval(() => this._loadBilling(), 30000);
    document.addEventListener('visibilitychange', this._onVisibility);
    window.addEventListener('billing:refresh', this._onBillingRefresh);
  }

  private _stopBillingPoll() {
    this._billingPollActive = false;
    if (this._billingTimer) { clearInterval(this._billingTimer); this._billingTimer = null; }
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('billing:refresh', this._onBillingRefresh);
  }

  private readonly _onVisibility = () => {
    if (document.hidden) {
      if (this._billingTimer) { clearInterval(this._billingTimer); this._billingTimer = null; }
    } else {
      this._loadBilling();
      if (!this._billingTimer) this._billingTimer = setInterval(() => this._loadBilling(), 30000);
    }
  };

  private readonly _onBillingRefresh = () => { this.userName = ''; this.userAvatar = ''; this._loadBilling(); };


  private get _concClass(): string {
    const avail = this.billingInfo.max_concurrency - this.billingInfo.running;
    if (avail <= 0 && this.billingInfo.max_concurrency > 0) return 'conc-full';
    if (avail <= 1) return 'conc-warn';
    return 'conc-ok';
  }
  private _fmtBytes(b: number): string {
    if (b >= 1073741824) return (b / 1073741824).toFixed(2) + 'GB';
    if (b >= 1048576) return (b / 1048576).toFixed(1) + 'MB';
    if (b >= 1024) return (b / 1024).toFixed(0) + 'KB';
    return b + 'B';
  }

  private async _loadBilling() {
    if (!AccountManager.isLoggedIn) return;
    try {
      const [res, tRes] = await Promise.all([
        api('/api/billing/info'),
        api('/api/billing/traffic'),
      ]);
      if (res.ok) {
        const data = await res.json();
        this.billingInfo = data;
        EventBus.emit('concurrency:change', {
          running: data.running,
          max: data.max_concurrency,
          available: data.max_concurrency - data.running,
        });
      }
      if (tRes.ok) {
        this.trafficInfo = await tRes.json();
      }
    } catch {}
    if (!this.userName) {
      try {
        const r = await api('/api/accounts/me/profile');
        if (r.ok) {
          const d = await r.json();
          this.userName = d.name || d.username || '';
          this.userAvatar = d.avatar || '';
        }
      } catch {}
    }
  }

  private getBreadcrumb(): string {
    const p = Router.getCurrentPath();
    const params = Router.getParams();
    if (p === '/' || p === '/dashboard') return '工作台';
    if (p === '/create') return '创建项目';
    if (p.startsWith('/project/regular')) {
      return params.id ? `常规漫剧 · 项目编辑` : '创建项目 · 常规漫剧';
    }
    if (p.startsWith('/project/new')) return '创建项目 · 新建项目';
    if (p.startsWith('/project/commentary-storyboard')) {
      return params.id ? `解说漫剧 · 分镜剧本` : '解说漫剧';
    }
    if (p.startsWith('/project/commentary')) return '创建项目 · 解说漫剧';
    if (p.startsWith('/projects')) return '项目管理';
    if (p.startsWith('/dubbing/select')) return '配音工作台';
    if (p.startsWith('/dubbing/')) return '解说漫剧 · 配音';
    if (p.startsWith('/storyboard')) {
      return params.id ? `分镜编辑` : '分镜编辑';
    }
    if (p.startsWith('/library/characters')) return '素材库 · 角色';
    if (p.startsWith('/library/sora-characters')) return '素材库 · Sora角色';
    if (p.startsWith('/library/scenes')) return '素材库 · 场景';
    if (p.startsWith('/library/props')) return '素材库 · 道具';
    if (p.startsWith('/library/prompts')) return '素材库 · 提示词模板';
    if (p.startsWith('/library/voices')) return '素材库 · 音色库';
    if (p.startsWith('/tasks')) return '任务记录';
    if (p.startsWith('/model-selector')) return '模型选择';
    if (p.startsWith('/account')) return '账户管理';
    if (p.startsWith('/billing')) return '我的账单';
    if (p.startsWith('/admin')) return '管理后台';
    return '';
  }

  override render() {
    return html`
      <div class="sidebar-overlay ${this.mobileOpen ? 'open' : ''}" @click=${() => { this.mobileOpen = false; }}></div>

      <aside class="sidebar ${this.collapsed ? 'collapsed' : ''} ${this.mobileOpen ? 'mobile-open' : ''}">
        <div class="brand" @click=${() => this.nav_to('/')}>
          <img class="brand-logo" src="/logo.png" alt="大言">
          <div class="brand-info">
            <span class="brand-tagline">AI Comic Studio</span>
          </div>
        </div>

        <div class="nav-scroll">
          <div class="nav-group-label">概览</div>
          ${this.renderNavItem(this.nav[0])}

          <div class="nav-group-label">创作</div>
          ${this.nav.slice(1, 5).map(i => this.renderNavItem(i))}

          <div class="nav-group-label">素材库</div>
          ${this.nav.slice(5, 11).map(i => this.renderNavItem(i))}

          <div class="nav-group-label">系统</div>
          ${this.nav.slice(11).map(i => this.renderNavItem(i))}
          ${AccountManager.isAdmin ? this.renderNavItem({ key: 'admin', icon: 'admin_panel_settings', label: '管理后台', path: '/admin' }) : nothing}
        </div>

        <div class="sidebar-footer">
          ${AccountManager.isLoggedIn ? html`
            <div class="user-row">
              <div class="user-avatar" @click=${() => Router.navigate('/account')}>
                ${this.userAvatar
                  ? html`<img src="${this.userAvatar}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" @error=${() => { this.userAvatar = ''; }}>`
                  : (this.userName || '?')[0].toUpperCase()}
              </div>
              <span class="user-name" @click=${() => Router.navigate('/account')}>${this.userName || '用户'}</span>
              <button class="logout-btn" style="width:auto;margin:0;padding:4px 8px;" @click=${() => AccountManager.logout()}>
                <span class="mi">logout</span>
              </button>
            </div>
            <div class="billing-bar ${this._concClass}" @click=${() => Router.navigate('/billing')}>
              <span class="billing-cell billing-cell-r"><span class="mi">bolt</span><span class="billing-val">${this.billingInfo.running}/${this.billingInfo.max_concurrency}</span></span>
              <span class="billing-sep">|</span>
              <span class="billing-cell billing-cell-l"><span class="mi">cloud</span><span class="billing-val">${this.billingInfo.storage_used ? (this.billingInfo.storage_used / 1073741824).toFixed(1) : '0'}/${this.billingInfo.max_storage_gb || 1}G</span></span>
              <span class="billing-cell billing-cell-r"><span class="mi">paid</span><span class="billing-val">¥${this.billingInfo.balance.toFixed(2)}</span></span>
              <span class="billing-sep">|</span>
              <span class="billing-cell billing-cell-l"><span class="mi">swap_vert</span><span class="billing-val">${this.trafficInfo ? this._fmtBytes(this.trafficInfo.total_bytes) : '0B'}</span></span>
            </div>

          ` : nothing}
          <button class="collapse-btn" @click=${() => this.toggle()}>
            <span class="mi" aria-hidden="true">chevron_left</span>
            ${this.collapsed ? nothing : html`<span>收起侧栏</span>`}
          </button>
        </div>
      </aside>

      <div class="main">
        <header class="main-header">
          <div style="display:flex;align-items:center;gap:10px;">
            <button class="header-btn mobile-toggle" @click=${() => this.toggle()}>
              <span class="mi" style="font-family:'Material Symbols Outlined';font-size:20px">menu</span>
            </button>
            <div class="breadcrumb">
              <span class="breadcrumb-current">${this.getBreadcrumb()}</span>
            </div>
          </div>
          <div class="header-actions">
            ${TaskTracker.activeCount() > 0 ? html`
              <div class="task-indicator" @click=${() => Router.go('/tasks')} title="\u70b9\u51fb\u67e5\u770b\u4efb\u52a1\u8bb0\u5f55">
                <span class="indicator-dot"></span>
                <span>${TaskTracker.activeCount()} \u4e2a\u4efb\u52a1\u8fd0\u884c\u4e2d</span>
              </div>
            ` : nothing}
            <button class="header-btn" @click=${() => this._cycleTheme()} title=${this._themeTitle}>
              <span style="font-family:'Material Symbols Outlined';font-size:18px">${this._themeIcon}</span>
            </button>
          </div>
        </header>
        <div class="main-content">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('manga-app')) {
  customElements.define('manga-app', MangaApp);
}

declare global {
  interface HTMLElementTagNameMap {
    'manga-app': MangaApp;
  }
}
